import { ReactElement, useMemo } from 'react';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';

import { IAiSettings, IOpenaiModelsResponse } from '~/services/api/ai/types';
import Button from '~/ui/components/common/Button';
import Tooltip from '~/ui/components/common/Tooltip';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import Textarea from '~/ui/components/inputs/Textarea';
import Toggle from '~/ui/components/inputs/Toggle';

import { IAiSettingsFormValues } from '~/ui/pages/AiManagement/types';

import { ReactComponent as InfoIcon } from '~/ui/assets/images/info.svg';

import {
  systemPromptDescription,
  systemPromptPlaceholder,
  temperatureDescription,
  tokensDescription,
} from '~/ui/pages/AiManagement/constants';
import toOptions from './utils';
import validate from './validate';

import variables from '~/ui/assets/styles/colors.module.scss';
import toggleStyles from '~/ui/components/inputs/Toggle/Toggle.module.scss';

import styles from './AiSettingsForm.module.scss';

interface IProps {
  title: string;
  loading?: boolean;
  isSystemLevel?: boolean;
  className?: string;
  formClassName?: string;
  defaultValues?: IAiSettings;
  aiModels?: IOpenaiModelsResponse | null;
  onSubmit: (val: IAiSettingsFormValues) => void;
}

const AiSettingsForm = ({
  title,
  loading,
  className,
  formClassName,
  defaultValues,
  aiModels,
  isSystemLevel = false,
  onSubmit,
}: IProps): ReactElement => {
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors, isDirty },
  } = useForm<IAiSettingsFormValues>({
    mode: 'onChange',
    resolver: validate,
    defaultValues,
  });

  const chatModelOptions = useMemo(() => toOptions(aiModels?.chatModels), [aiModels?.chatModels]);

  /* Speech to text model is hidden for now, for the backend we send first option as default, because it's required on the backend side */
  // const speechToTextModelOptions = useMemo(
  //   () => toOptions(aiModels?.speechToTextModels),
  //   [aiModels?.speechToTextModels],
  // );

  const [systemPrompt, temperature, maxTokens, active, selectedChatModelName] = watch([
    'systemPrompt',
    'temperature',
    'maxTokens',
    'active',
    'chatModelName',
  ]);

  const isTemperatureDisabled =
    !active ||
    !aiModels?.chatModels?.find(option => option.name === selectedChatModelName)
      ?.supportsTemperature;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={formClassName}>
      <Paper className={classNames(styles.aiContainer, className)}>
        <div className={styles.row}>
          <h2 className={styles.title}>{title}</h2>
          {!isSystemLevel && (
            <Box
              className={classNames(styles.toggle, toggleStyles.toggleContainer)}
              sx={{
                marginTop: 1,
                alignItems: 'center',
              }}
            >
              <Toggle
                name="active"
                size="small"
                control={control}
                label="Active"
                errors={errors}
                labelPlacement="start"
              />
            </Box>
          )}
        </div>

        <div className={styles.inputWrapper}>
          <Textarea
            register={register}
            errors={errors}
            name="systemPrompt"
            label="System Prompt"
            placeholder={`Example: ${systemPromptPlaceholder}`}
            disabled={!active}
          />
          <div>
            <Tooltip
              icon={<InfoIcon color={variables.colorBlack} width={20} />}
              body={<Typography>{systemPromptDescription}</Typography>}
              className={styles.formTooltip}
            />
          </div>
        </div>
        <div className={styles.inputWrapper}>
          <Input
            register={register}
            errors={errors}
            name="temperature"
            label="Temperature"
            placeholder="Example: 0.3"
            disabled={isTemperatureDisabled}
          />
          <div>
            <Tooltip
              icon={<InfoIcon color={variables.colorBlack} width={20} />}
              body={<Typography>{temperatureDescription}</Typography>}
              className={styles.formTooltip}
            />
          </div>
        </div>
        <div className={styles.inputWrapper}>
          <Input
            register={register}
            errors={errors}
            name="maxTokens"
            label="Max Tokens"
            placeholder="Example: 300"
            disabled={!active}
          />
          <div>
            <Tooltip
              icon={<InfoIcon color={variables.colorBlack} width={20} />}
              body={<Typography>{tokensDescription}</Typography>}
              className={styles.formTooltip}
            />
          </div>
        </div>
        {chatModelOptions.length > 0 && (
          <div className={styles.inputWrapper}>
            <Select
              control={control}
              errors={errors}
              name="chatModelName"
              options={chatModelOptions}
              label="Chat Model"
              placeholder="Select chat model"
              isDisabled={!active}
              hideSelectedOptions={false}
            />
          </div>
        )}

        {/* Speech to text model is hidden for now, for the backend we send first option as default, because it's required on the backend side */}
        {/* {speechToTextModelOptions.length > 0 && (
          <div className={styles.inputWrapper}>
            <Select
              control={control}
              errors={errors}
              name="speechToTextModelName"
              options={speechToTextModelOptions}
              label="Speech to Text Model"
              placeholder="Select speech to text model"
              isDisabled={!active}
              hideSelectedOptions={false}
            />
          </div>
        )} */}

        <div className={styles.buttonWrapper}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            isLoading={loading}
            loaderColor={variables.colorWhite}
            disabled={!systemPrompt || !temperature || !maxTokens || !isDirty}
          >
            Save
          </Button>
        </div>
      </Paper>
    </form>
  );
};

export default AiSettingsForm;
